import * as z from 'zod';

export const QueryTicketDefinitionsRequest = z.object({
  options: z
    .object({
      offset: z
        .number()
        .int()
        .describe(
          'Offset. See [Pagination](https://dev.wix.com/api/rest/getting-started/pagination).'
        )
        .min(0)
        .optional(),
      limit: z
        .number()
        .int()
        .describe(
          'Limit. See [Pagination](https://dev.wix.com/api/rest/getting-started/pagination).'
        )
        .min(0)
        .max(100)
        .optional(),
      fieldset: z
        .array(z.enum(['POLICY', 'DASHBOARD']))
        .max(20)
        .optional(),
      filter: z
        .record(z.string(), z.any())
        .describe('Filter.')
        .optional()
        .nullable(),
      sort: z
        .string()
        .describe('Sort order. Defaults to: "created:asc".')
        .max(100)
        .optional(),
      facet: z.array(z.string()).max(10).optional(),
    })
    .optional(),
});
export const QueryTicketDefinitionsResponse = z.object({
  total: z
    .number()
    .int()
    .describe('Total ticket definitions matching the given filters.')
    .optional(),
  offset: z.number().int().describe('Offset.').min(0).optional(),
  limit: z.number().int().describe('Limit.').min(0).max(100).optional(),
  definitions: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Ticket definition ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        price: z
          .object({
            amount: z
              .string()
              .describe('*Deprecated:** Use `value` instead.')
              .optional(),
            currency: z
              .string()
              .describe(
                '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
              )
              .optional(),
            value: z
              .string()
              .describe(
                'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
              )
              .optional()
              .nullable(),
          })
          .describe('Ticket price.')
          .optional(),
        free: z
          .boolean()
          .describe('Whether the ticket is free (read only).')
          .optional(),
        name: z.string().describe('Ticket name.').optional(),
        description: z.string().describe('Ticket description.').optional(),
        limitPerCheckout: z
          .number()
          .int()
          .describe(
            'Limit of tickets that can be purchased per checkout.\nSet to 20 for unlimited ticket definition.'
          )
          .min(0)
          .max(20)
          .optional(),
        orderIndex: z.number().int().describe('Custom sort index.').optional(),
        policy: z
          .string()
          .describe(
            'Policy information plain text block, as printed on the ticket.'
          )
          .optional(),
        dashboard: z
          .object({
            hidden: z
              .boolean()
              .describe('Whether ticket is hidden and cannot be sold.')
              .optional(),
            sold: z
              .number()
              .int()
              .describe('Number of tickets sold and reserved.')
              .optional(),
            limited: z
              .boolean()
              .describe('Whether the ticket has limited quantity.')
              .optional(),
            quantity: z
              .number()
              .int()
              .describe(
                'Ticket limit. `NULL` for unlimited ticket definitions.'
              )
              .optional()
              .nullable(),
            unsold: z
              .number()
              .int()
              .describe(
                'Number of unsold tickets. `NULL` for unlimited ticket definitions.'
              )
              .optional()
              .nullable(),
            ticketsSold: z
              .number()
              .int()
              .describe('Number of tickets sold.')
              .optional(),
            ticketsReserved: z
              .number()
              .int()
              .describe('Number of tickets reserved.')
              .optional(),
          })
          .describe('Sensitive dashboard data.')
          .optional(),
        eventId: z
          .string()
          .describe('Event ID associated with the ticket.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        wixFeeConfig: z
          .object({
            type: z
              .enum(['FEE_ADDED', 'FEE_INCLUDED', 'FEE_ADDED_AT_CHECKOUT'])
              .describe('Fee calculation method.')
              .optional(),
          })
          .describe(
            'Configuration of the fixed-rate Wix service fee that is applied at checkout to each ticket sold.'
          )
          .optional(),
        salePeriod: z
          .object({
            startDate: z
              .date()
              .describe('Ticket sale start timestamp.')
              .optional()
              .nullable(),
            endDate: z
              .date()
              .describe('Ticket sale end timestamp.')
              .optional()
              .nullable(),
            hideNotOnSale: z
              .boolean()
              .describe("Whether to hide this ticket if it isn't on sale.")
              .optional(),
          })
          .describe('Ticket sale period.')
          .optional(),
        saleStatus: z
          .enum(['SALE_SCHEDULED', 'SALE_STARTED', 'SALE_ENDED'])
          .describe('Ticket sale status.')
          .optional(),
        state: z.array(z.enum(['INCLUDE_HIDDEN_NOT_ON_SALE'])).optional(),
        pricing: z
          .intersection(
            z.object({
              pricingType: z
                .enum(['STANDARD', 'DONATION'])
                .describe('Ticket pricing type.')
                .optional(),
            }),
            z.xor([
              z.object({
                fixedPrice: z.never().optional(),
                minPrice: z.never().optional(),
                pricingOptions: z.never().optional(),
              }),
              z.object({
                minPrice: z.never().optional(),
                pricingOptions: z.never().optional(),
                fixedPrice: z
                  .object({
                    amount: z
                      .string()
                      .describe('*Deprecated:** Use `value` instead.')
                      .optional(),
                    currency: z
                      .string()
                      .describe(
                        '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
                      )
                      .optional(),
                    value: z
                      .string()
                      .describe(
                        'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe('Ticket price which is read only.'),
              }),
              z.object({
                fixedPrice: z.never().optional(),
                pricingOptions: z.never().optional(),
                minPrice: z
                  .object({
                    amount: z
                      .string()
                      .describe('*Deprecated:** Use `value` instead.')
                      .optional(),
                    currency: z
                      .string()
                      .describe(
                        '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
                      )
                      .optional(),
                    value: z
                      .string()
                      .describe(
                        'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe('Min price per ticket, customizable.'),
              }),
              z.object({
                fixedPrice: z.never().optional(),
                minPrice: z.never().optional(),
                pricingOptions: z
                  .object({
                    options: z
                      .array(
                        z.object({
                          _id: z
                            .string()
                            .describe('Ticket pricing option ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          name: z
                            .string()
                            .describe('Ticket pricing option name.')
                            .min(1)
                            .max(200)
                            .optional()
                            .nullable(),
                          price: z
                            .object({
                              amount: z
                                .string()
                                .describe('*Deprecated:** Use `value` instead.')
                                .optional(),
                              currency: z
                                .string()
                                .describe(
                                  '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
                                )
                                .optional(),
                              value: z
                                .string()
                                .describe(
                                  'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
                                )
                                .optional()
                                .nullable(),
                            })
                            .describe('Ticket pricing option price.')
                            .optional(),
                        })
                      )
                      .optional(),
                  })
                  .describe('Ticket pricing options.'),
              }),
            ])
          )
          .describe('Ticket pricing.')
          .optional(),
      })
    )
    .optional(),
  facets: z
    .record(
      z.string(),
      z.object({
        counts: z
          .record(z.string(), z.number().int())
          .describe('Facet counts aggregated per value.')
          .optional(),
      })
    )
    .describe('Filter facets.')
    .optional(),
});
export const QueryTicketDefinitionsV2Request = z.object({
  query: z
    .intersection(
      z.object({
        filter: z
          .record(z.string(), z.any())
          .describe(
            'Filter object.\n\nLearn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).'
          )
          .optional()
          .nullable(),
        sort: z
          .array(
            z.object({
              fieldName: z
                .string()
                .describe('Name of the field to sort by.')
                .max(512)
                .optional(),
              order: z.enum(['ASC', 'DESC']).optional(),
            })
          )
          .optional(),
      }),
      z.xor([
        z.object({
          paging: z.never().optional(),
          cursorPaging: z.never().optional(),
        }),
        z.object({
          cursorPaging: z.never().optional(),
          paging: z
            .object({
              limit: z
                .number()
                .int()
                .describe('Number of items to load.')
                .min(0)
                .optional()
                .nullable(),
              offset: z
                .number()
                .int()
                .describe('Number of items to skip in the current sort order.')
                .min(0)
                .optional()
                .nullable(),
            })
            .describe(
              'Paging options to limit and offset the number of items.'
            ),
        }),
        z.object({
          paging: z.never().optional(),
          cursorPaging: z
            .object({
              limit: z
                .number()
                .int()
                .describe('Maximum number of items to return in the results.')
                .min(0)
                .max(100)
                .optional()
                .nullable(),
              cursor: z
                .string()
                .describe(
                  "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
                )
                .max(16000)
                .optional()
                .nullable(),
            })
            .describe(
              'Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.'
            ),
        }),
      ])
    )
    .describe('Query request object.'),
});
export const QueryTicketDefinitionsV2Response = z.object({
  definitions: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Ticket definition ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        price: z
          .object({
            amount: z
              .string()
              .describe('*Deprecated:** Use `value` instead.')
              .optional(),
            currency: z
              .string()
              .describe(
                '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
              )
              .optional(),
            value: z
              .string()
              .describe(
                'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
              )
              .optional()
              .nullable(),
          })
          .describe('Ticket price.')
          .optional(),
        free: z
          .boolean()
          .describe('Whether the ticket is free (read only).')
          .optional(),
        name: z.string().describe('Ticket name.').optional(),
        description: z.string().describe('Ticket description.').optional(),
        limitPerCheckout: z
          .number()
          .int()
          .describe(
            'Limit of tickets that can be purchased per checkout.\nSet to 20 for unlimited ticket definition.'
          )
          .min(0)
          .max(20)
          .optional(),
        orderIndex: z.number().int().describe('Custom sort index.').optional(),
        policy: z
          .string()
          .describe(
            'Policy information plain text block, as printed on the ticket.'
          )
          .optional(),
        dashboard: z
          .object({
            hidden: z
              .boolean()
              .describe('Whether ticket is hidden and cannot be sold.')
              .optional(),
            sold: z
              .number()
              .int()
              .describe('Number of tickets sold and reserved.')
              .optional(),
            limited: z
              .boolean()
              .describe('Whether the ticket has limited quantity.')
              .optional(),
            quantity: z
              .number()
              .int()
              .describe(
                'Ticket limit. `NULL` for unlimited ticket definitions.'
              )
              .optional()
              .nullable(),
            unsold: z
              .number()
              .int()
              .describe(
                'Number of unsold tickets. `NULL` for unlimited ticket definitions.'
              )
              .optional()
              .nullable(),
            ticketsSold: z
              .number()
              .int()
              .describe('Number of tickets sold.')
              .optional(),
            ticketsReserved: z
              .number()
              .int()
              .describe('Number of tickets reserved.')
              .optional(),
          })
          .describe('Sensitive dashboard data.')
          .optional(),
        eventId: z
          .string()
          .describe('Event ID associated with the ticket.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        wixFeeConfig: z
          .object({
            type: z
              .enum(['FEE_ADDED', 'FEE_INCLUDED', 'FEE_ADDED_AT_CHECKOUT'])
              .describe('Fee calculation method.')
              .optional(),
          })
          .describe(
            'Configuration of the fixed-rate Wix service fee that is applied at checkout to each ticket sold.'
          )
          .optional(),
        salePeriod: z
          .object({
            startDate: z
              .date()
              .describe('Ticket sale start timestamp.')
              .optional()
              .nullable(),
            endDate: z
              .date()
              .describe('Ticket sale end timestamp.')
              .optional()
              .nullable(),
            hideNotOnSale: z
              .boolean()
              .describe("Whether to hide this ticket if it isn't on sale.")
              .optional(),
          })
          .describe('Ticket sale period.')
          .optional(),
        saleStatus: z
          .enum(['SALE_SCHEDULED', 'SALE_STARTED', 'SALE_ENDED'])
          .describe('Ticket sale status.')
          .optional(),
        state: z.array(z.enum(['INCLUDE_HIDDEN_NOT_ON_SALE'])).optional(),
        pricing: z
          .intersection(
            z.object({
              pricingType: z
                .enum(['STANDARD', 'DONATION'])
                .describe('Ticket pricing type.')
                .optional(),
            }),
            z.xor([
              z.object({
                fixedPrice: z.never().optional(),
                minPrice: z.never().optional(),
                pricingOptions: z.never().optional(),
              }),
              z.object({
                minPrice: z.never().optional(),
                pricingOptions: z.never().optional(),
                fixedPrice: z
                  .object({
                    amount: z
                      .string()
                      .describe('*Deprecated:** Use `value` instead.')
                      .optional(),
                    currency: z
                      .string()
                      .describe(
                        '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
                      )
                      .optional(),
                    value: z
                      .string()
                      .describe(
                        'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe('Ticket price which is read only.'),
              }),
              z.object({
                fixedPrice: z.never().optional(),
                pricingOptions: z.never().optional(),
                minPrice: z
                  .object({
                    amount: z
                      .string()
                      .describe('*Deprecated:** Use `value` instead.')
                      .optional(),
                    currency: z
                      .string()
                      .describe(
                        '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
                      )
                      .optional(),
                    value: z
                      .string()
                      .describe(
                        'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe('Min price per ticket, customizable.'),
              }),
              z.object({
                fixedPrice: z.never().optional(),
                minPrice: z.never().optional(),
                pricingOptions: z
                  .object({
                    options: z
                      .array(
                        z.object({
                          _id: z
                            .string()
                            .describe('Ticket pricing option ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          name: z
                            .string()
                            .describe('Ticket pricing option name.')
                            .min(1)
                            .max(200)
                            .optional()
                            .nullable(),
                          price: z
                            .object({
                              amount: z
                                .string()
                                .describe('*Deprecated:** Use `value` instead.')
                                .optional(),
                              currency: z
                                .string()
                                .describe(
                                  '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
                                )
                                .optional(),
                              value: z
                                .string()
                                .describe(
                                  'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
                                )
                                .optional()
                                .nullable(),
                            })
                            .describe('Ticket pricing option price.')
                            .optional(),
                        })
                      )
                      .optional(),
                  })
                  .describe('Ticket pricing options.'),
              }),
            ])
          )
          .describe('Ticket pricing.')
          .optional(),
      })
    )
    .optional(),
  metadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Offset that was requested.')
        .optional()
        .nullable(),
      total: z
        .number()
        .int()
        .describe(
          'Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set.'
        )
        .optional()
        .nullable(),
      tooManyToCount: z
        .boolean()
        .describe(
          'Flag that indicates the server failed to calculate the `total` field.'
        )
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used.'
        )
        .optional(),
    })
    .describe('Paging metadata definitions.')
    .optional(),
});
export const ListTicketDefinitionsRequest = z.object({
  options: z
    .object({
      eventId: z.array(z.string()).max(100).optional(),
      offset: z
        .number()
        .int()
        .describe(
          'Offset. See [Pagination](https://dev.wix.com/api/rest/getting-started/pagination).'
        )
        .min(0)
        .optional(),
      limit: z
        .number()
        .int()
        .describe(
          'Limit. See [Pagination](https://dev.wix.com/api/rest/getting-started/pagination).'
        )
        .min(0)
        .max(100)
        .optional(),
      fieldset: z
        .array(z.enum(['POLICY', 'DASHBOARD']))
        .max(20)
        .optional(),
      eventCreatorId: z.array(z.string()).max(1).optional(),
      state: z
        .array(z.enum(['VISIBLE', 'HIDDEN', 'FREE', 'PAID']))
        .max(20)
        .optional(),
      sort: z
        .string()
        .describe('Sort order. Defaults to: "created:asc".')
        .max(100)
        .optional(),
      saleStatus: z
        .array(z.enum(['SALE_SCHEDULED', 'SALE_STARTED', 'SALE_ENDED']))
        .max(5)
        .optional(),
      facet: z.array(z.string()).max(10).optional(),
    })
    .describe('Details for the tickets to retrieve.')
    .optional(),
});
export const ListTicketDefinitionsResponse = z.object({
  metaData: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items in the response.')
        .optional(),
      offset: z.number().int().describe('Offset of items.').min(0).optional(),
      total: z
        .number()
        .int()
        .describe('Total number of matching items.')
        .optional(),
    })
    .describe('Meta data.')
    .optional(),
  definitions: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Ticket definition ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        price: z
          .object({
            amount: z
              .string()
              .describe('*Deprecated:** Use `value` instead.')
              .optional(),
            currency: z
              .string()
              .describe(
                '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
              )
              .optional(),
            value: z
              .string()
              .describe(
                'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
              )
              .optional()
              .nullable(),
          })
          .describe('Ticket price.')
          .optional(),
        free: z
          .boolean()
          .describe('Whether the ticket is free (read only).')
          .optional(),
        name: z.string().describe('Ticket name.').optional(),
        description: z.string().describe('Ticket description.').optional(),
        limitPerCheckout: z
          .number()
          .int()
          .describe(
            'Limit of tickets that can be purchased per checkout.\nSet to 20 for unlimited ticket definition.'
          )
          .min(0)
          .max(20)
          .optional(),
        orderIndex: z.number().int().describe('Custom sort index.').optional(),
        policy: z
          .string()
          .describe(
            'Policy information plain text block, as printed on the ticket.'
          )
          .optional(),
        dashboard: z
          .object({
            hidden: z
              .boolean()
              .describe('Whether ticket is hidden and cannot be sold.')
              .optional(),
            sold: z
              .number()
              .int()
              .describe('Number of tickets sold and reserved.')
              .optional(),
            limited: z
              .boolean()
              .describe('Whether the ticket has limited quantity.')
              .optional(),
            quantity: z
              .number()
              .int()
              .describe(
                'Ticket limit. `NULL` for unlimited ticket definitions.'
              )
              .optional()
              .nullable(),
            unsold: z
              .number()
              .int()
              .describe(
                'Number of unsold tickets. `NULL` for unlimited ticket definitions.'
              )
              .optional()
              .nullable(),
            ticketsSold: z
              .number()
              .int()
              .describe('Number of tickets sold.')
              .optional(),
            ticketsReserved: z
              .number()
              .int()
              .describe('Number of tickets reserved.')
              .optional(),
          })
          .describe('Sensitive dashboard data.')
          .optional(),
        eventId: z
          .string()
          .describe('Event ID associated with the ticket.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        wixFeeConfig: z
          .object({
            type: z
              .enum(['FEE_ADDED', 'FEE_INCLUDED', 'FEE_ADDED_AT_CHECKOUT'])
              .describe('Fee calculation method.')
              .optional(),
          })
          .describe(
            'Configuration of the fixed-rate Wix service fee that is applied at checkout to each ticket sold.'
          )
          .optional(),
        salePeriod: z
          .object({
            startDate: z
              .date()
              .describe('Ticket sale start timestamp.')
              .optional()
              .nullable(),
            endDate: z
              .date()
              .describe('Ticket sale end timestamp.')
              .optional()
              .nullable(),
            hideNotOnSale: z
              .boolean()
              .describe("Whether to hide this ticket if it isn't on sale.")
              .optional(),
          })
          .describe('Ticket sale period.')
          .optional(),
        saleStatus: z
          .enum(['SALE_SCHEDULED', 'SALE_STARTED', 'SALE_ENDED'])
          .describe('Ticket sale status.')
          .optional(),
        state: z.array(z.enum(['INCLUDE_HIDDEN_NOT_ON_SALE'])).optional(),
        pricing: z
          .intersection(
            z.object({
              pricingType: z
                .enum(['STANDARD', 'DONATION'])
                .describe('Ticket pricing type.')
                .optional(),
            }),
            z.xor([
              z.object({
                fixedPrice: z.never().optional(),
                minPrice: z.never().optional(),
                pricingOptions: z.never().optional(),
              }),
              z.object({
                minPrice: z.never().optional(),
                pricingOptions: z.never().optional(),
                fixedPrice: z
                  .object({
                    amount: z
                      .string()
                      .describe('*Deprecated:** Use `value` instead.')
                      .optional(),
                    currency: z
                      .string()
                      .describe(
                        '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
                      )
                      .optional(),
                    value: z
                      .string()
                      .describe(
                        'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe('Ticket price which is read only.'),
              }),
              z.object({
                fixedPrice: z.never().optional(),
                pricingOptions: z.never().optional(),
                minPrice: z
                  .object({
                    amount: z
                      .string()
                      .describe('*Deprecated:** Use `value` instead.')
                      .optional(),
                    currency: z
                      .string()
                      .describe(
                        '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
                      )
                      .optional(),
                    value: z
                      .string()
                      .describe(
                        'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe('Min price per ticket, customizable.'),
              }),
              z.object({
                fixedPrice: z.never().optional(),
                minPrice: z.never().optional(),
                pricingOptions: z
                  .object({
                    options: z
                      .array(
                        z.object({
                          _id: z
                            .string()
                            .describe('Ticket pricing option ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          name: z
                            .string()
                            .describe('Ticket pricing option name.')
                            .min(1)
                            .max(200)
                            .optional()
                            .nullable(),
                          price: z
                            .object({
                              amount: z
                                .string()
                                .describe('*Deprecated:** Use `value` instead.')
                                .optional(),
                              currency: z
                                .string()
                                .describe(
                                  '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
                                )
                                .optional(),
                              value: z
                                .string()
                                .describe(
                                  'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
                                )
                                .optional()
                                .nullable(),
                            })
                            .describe('Ticket pricing option price.')
                            .optional(),
                        })
                      )
                      .optional(),
                  })
                  .describe('Ticket pricing options.'),
              }),
            ])
          )
          .describe('Ticket pricing.')
          .optional(),
      })
    )
    .optional(),
  facets: z
    .record(
      z.string(),
      z.object({
        counts: z
          .record(z.string(), z.number().int())
          .describe('Facet counts aggregated per value.')
          .optional(),
      })
    )
    .describe('Filter facets.')
    .optional(),
});
export const GetTicketDefinitionRequest = z.object({
  definitionId: z
    .string()
    .describe('Ticket definition ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      fieldset: z
        .array(z.enum(['POLICY', 'DASHBOARD']))
        .max(20)
        .optional(),
    })
    .describe('Details for the ticket to retrieve.')
    .optional(),
});
export const GetTicketDefinitionResponse = z.object({
  _id: z
    .string()
    .describe('Ticket definition ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  price: z
    .object({
      amount: z
        .string()
        .describe('*Deprecated:** Use `value` instead.')
        .optional(),
      currency: z
        .string()
        .describe(
          '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
        )
        .optional(),
      value: z
        .string()
        .describe(
          'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
        )
        .optional()
        .nullable(),
    })
    .describe('Ticket price.')
    .optional(),
  free: z
    .boolean()
    .describe('Whether the ticket is free (read only).')
    .optional(),
  name: z.string().describe('Ticket name.').optional(),
  description: z.string().describe('Ticket description.').optional(),
  limitPerCheckout: z
    .number()
    .int()
    .describe(
      'Limit of tickets that can be purchased per checkout.\nSet to 20 for unlimited ticket definition.'
    )
    .min(0)
    .max(20)
    .optional(),
  orderIndex: z.number().int().describe('Custom sort index.').optional(),
  policy: z
    .string()
    .describe('Policy information plain text block, as printed on the ticket.')
    .optional(),
  dashboard: z
    .object({
      hidden: z
        .boolean()
        .describe('Whether ticket is hidden and cannot be sold.')
        .optional(),
      sold: z
        .number()
        .int()
        .describe('Number of tickets sold and reserved.')
        .optional(),
      limited: z
        .boolean()
        .describe('Whether the ticket has limited quantity.')
        .optional(),
      quantity: z
        .number()
        .int()
        .describe('Ticket limit. `NULL` for unlimited ticket definitions.')
        .optional()
        .nullable(),
      unsold: z
        .number()
        .int()
        .describe(
          'Number of unsold tickets. `NULL` for unlimited ticket definitions.'
        )
        .optional()
        .nullable(),
      ticketsSold: z
        .number()
        .int()
        .describe('Number of tickets sold.')
        .optional(),
      ticketsReserved: z
        .number()
        .int()
        .describe('Number of tickets reserved.')
        .optional(),
    })
    .describe('Sensitive dashboard data.')
    .optional(),
  eventId: z
    .string()
    .describe('Event ID associated with the ticket.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  wixFeeConfig: z
    .object({
      type: z
        .enum(['FEE_ADDED', 'FEE_INCLUDED', 'FEE_ADDED_AT_CHECKOUT'])
        .describe('Fee calculation method.')
        .optional(),
    })
    .describe(
      'Configuration of the fixed-rate Wix service fee that is applied at checkout to each ticket sold.'
    )
    .optional(),
  salePeriod: z
    .object({
      startDate: z
        .date()
        .describe('Ticket sale start timestamp.')
        .optional()
        .nullable(),
      endDate: z
        .date()
        .describe('Ticket sale end timestamp.')
        .optional()
        .nullable(),
      hideNotOnSale: z
        .boolean()
        .describe("Whether to hide this ticket if it isn't on sale.")
        .optional(),
    })
    .describe('Ticket sale period.')
    .optional(),
  saleStatus: z
    .enum(['SALE_SCHEDULED', 'SALE_STARTED', 'SALE_ENDED'])
    .describe('Ticket sale status.')
    .optional(),
  state: z.array(z.enum(['INCLUDE_HIDDEN_NOT_ON_SALE'])).optional(),
  pricing: z
    .intersection(
      z.object({
        pricingType: z
          .enum(['STANDARD', 'DONATION'])
          .describe('Ticket pricing type.')
          .optional(),
      }),
      z.xor([
        z.object({
          fixedPrice: z.never().optional(),
          minPrice: z.never().optional(),
          pricingOptions: z.never().optional(),
        }),
        z.object({
          minPrice: z.never().optional(),
          pricingOptions: z.never().optional(),
          fixedPrice: z
            .object({
              amount: z
                .string()
                .describe('*Deprecated:** Use `value` instead.')
                .optional(),
              currency: z
                .string()
                .describe(
                  '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
                )
                .optional(),
              value: z
                .string()
                .describe(
                  'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
                )
                .optional()
                .nullable(),
            })
            .describe('Ticket price which is read only.'),
        }),
        z.object({
          fixedPrice: z.never().optional(),
          pricingOptions: z.never().optional(),
          minPrice: z
            .object({
              amount: z
                .string()
                .describe('*Deprecated:** Use `value` instead.')
                .optional(),
              currency: z
                .string()
                .describe(
                  '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
                )
                .optional(),
              value: z
                .string()
                .describe(
                  'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
                )
                .optional()
                .nullable(),
            })
            .describe('Min price per ticket, customizable.'),
        }),
        z.object({
          fixedPrice: z.never().optional(),
          minPrice: z.never().optional(),
          pricingOptions: z
            .object({
              options: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Ticket pricing option ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    name: z
                      .string()
                      .describe('Ticket pricing option name.')
                      .min(1)
                      .max(200)
                      .optional()
                      .nullable(),
                    price: z
                      .object({
                        amount: z
                          .string()
                          .describe('*Deprecated:** Use `value` instead.')
                          .optional(),
                        currency: z
                          .string()
                          .describe(
                            '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
                          )
                          .optional(),
                        value: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe('Ticket pricing option price.')
                      .optional(),
                  })
                )
                .optional(),
            })
            .describe('Ticket pricing options.'),
        }),
      ])
    )
    .describe('Ticket pricing.')
    .optional(),
});
export const CreateTicketDefinitionRequest = z.object({
  eventId: z
    .string()
    .describe('Event ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z.object({
    definition: z
      .object({
        name: z.string().describe('Ticket name.').max(30),
        price: z
          .object({
            amount: z
              .string()
              .describe('*Deprecated:** Use `value` instead.')
              .optional(),
            currency: z
              .string()
              .describe(
                '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
              )
              .optional(),
            value: z
              .string()
              .describe(
                'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
              )
              .optional()
              .nullable(),
          })
          .describe('Ticket price.')
          .optional(),
        description: z
          .string()
          .describe('Ticket description.')
          .max(500)
          .optional()
          .nullable(),
        limited: z
          .boolean()
          .describe('Whether this ticket type is limited in quantity.')
          .optional(),
        quantity: z
          .number()
          .int()
          .describe('Limit for this ticket type.\n\n`NULL` for unlimited.')
          .optional()
          .nullable(),
        orderIndex: z
          .number()
          .int()
          .describe(
            'Custom sort index for manual tickets ordering implementation.'
          )
          .optional(),
        policy: z
          .string()
          .describe(
            'Policy information in plain text (as listed on the ticket).'
          )
          .max(1000)
          .optional()
          .nullable(),
        hidden: z
          .boolean()
          .describe(
            'Whether this ticket type is hidden to customers and cannot be purchased.'
          )
          .optional(),
        wixFeeConfig: z
          .object({
            type: z
              .enum(['FEE_ADDED', 'FEE_INCLUDED', 'FEE_ADDED_AT_CHECKOUT'])
              .optional(),
          })
          .describe(
            'Configuration of the fixed-rate Wix service fee that is applied to each ticket sold.\n\nDefault: `FEE_ADDED_AT_CHECKOUT`.'
          )
          .optional(),
        salePeriod: z
          .object({
            startDate: z
              .date()
              .describe('Ticket sale start timestamp.')
              .optional()
              .nullable(),
            endDate: z
              .date()
              .describe('Ticket sale end timestamp.')
              .optional()
              .nullable(),
            hideNotOnSale: z
              .boolean()
              .describe("Whether to hide this ticket if it isn't on sale.")
              .optional(),
          })
          .describe('Ticket sale period.')
          .optional(),
        pricing: z
          .intersection(
            z.object({
              pricingType: z.enum(['STANDARD', 'DONATION']).optional(),
            }),
            z.xor([
              z.object({
                fixedPrice: z.never().optional(),
                minPrice: z.never().optional(),
                pricingOptions: z.never().optional(),
              }),
              z.object({
                minPrice: z.never().optional(),
                pricingOptions: z.never().optional(),
                fixedPrice: z
                  .object({
                    amount: z
                      .string()
                      .describe('*Deprecated:** Use `value` instead.')
                      .optional(),
                    currency: z
                      .string()
                      .describe(
                        '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
                      )
                      .optional(),
                    value: z
                      .string()
                      .describe(
                        'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe('Ticket price which is read only.'),
              }),
              z.object({
                fixedPrice: z.never().optional(),
                pricingOptions: z.never().optional(),
                minPrice: z
                  .object({
                    amount: z
                      .string()
                      .describe('*Deprecated:** Use `value` instead.')
                      .optional(),
                    currency: z
                      .string()
                      .describe(
                        '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
                      )
                      .optional(),
                    value: z
                      .string()
                      .describe(
                        'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe('Min price per ticket, customizable.'),
              }),
              z.object({
                fixedPrice: z.never().optional(),
                minPrice: z.never().optional(),
                pricingOptions: z
                  .object({
                    options: z
                      .array(
                        z.object({
                          _id: z
                            .string()
                            .describe('Ticket pricing option ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          name: z
                            .string()
                            .describe('Ticket pricing option name.')
                            .min(1)
                            .max(200)
                            .optional()
                            .nullable(),
                          price: z
                            .object({
                              amount: z
                                .string()
                                .describe('*Deprecated:** Use `value` instead.')
                                .optional(),
                              currency: z
                                .string()
                                .describe(
                                  '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
                                )
                                .optional(),
                              value: z
                                .string()
                                .describe(
                                  'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
                                )
                                .optional()
                                .nullable(),
                            })
                            .describe('Ticket pricing option price.')
                            .optional(),
                        })
                      )
                      .optional(),
                  })
                  .describe('Ticket pricing options.'),
              }),
            ])
          )
          .describe('Ticket pricing.')
          .optional(),
      })
      .describe('Ticket definition data.'),
  }),
});
export const CreateTicketDefinitionResponse = z.object({
  definition: z
    .object({
      _id: z
        .string()
        .describe('Ticket definition ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      price: z
        .object({
          amount: z
            .string()
            .describe('*Deprecated:** Use `value` instead.')
            .optional(),
          currency: z
            .string()
            .describe(
              '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
            )
            .optional(),
          value: z
            .string()
            .describe(
              'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
            )
            .optional()
            .nullable(),
        })
        .describe('Ticket price.')
        .optional(),
      free: z
        .boolean()
        .describe('Whether the ticket is free (read only).')
        .optional(),
      name: z.string().describe('Ticket name.').optional(),
      description: z.string().describe('Ticket description.').optional(),
      limitPerCheckout: z
        .number()
        .int()
        .describe(
          'Limit of tickets that can be purchased per checkout.\nSet to 20 for unlimited ticket definition.'
        )
        .min(0)
        .max(20)
        .optional(),
      orderIndex: z.number().int().describe('Custom sort index.').optional(),
      policy: z
        .string()
        .describe(
          'Policy information plain text block, as printed on the ticket.'
        )
        .optional(),
      dashboard: z
        .object({
          hidden: z
            .boolean()
            .describe('Whether ticket is hidden and cannot be sold.')
            .optional(),
          sold: z
            .number()
            .int()
            .describe('Number of tickets sold and reserved.')
            .optional(),
          limited: z
            .boolean()
            .describe('Whether the ticket has limited quantity.')
            .optional(),
          quantity: z
            .number()
            .int()
            .describe('Ticket limit. `NULL` for unlimited ticket definitions.')
            .optional()
            .nullable(),
          unsold: z
            .number()
            .int()
            .describe(
              'Number of unsold tickets. `NULL` for unlimited ticket definitions.'
            )
            .optional()
            .nullable(),
          ticketsSold: z
            .number()
            .int()
            .describe('Number of tickets sold.')
            .optional(),
          ticketsReserved: z
            .number()
            .int()
            .describe('Number of tickets reserved.')
            .optional(),
        })
        .describe('Sensitive dashboard data.')
        .optional(),
      eventId: z
        .string()
        .describe('Event ID associated with the ticket.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      wixFeeConfig: z
        .object({
          type: z
            .enum(['FEE_ADDED', 'FEE_INCLUDED', 'FEE_ADDED_AT_CHECKOUT'])
            .describe('Fee calculation method.')
            .optional(),
        })
        .describe(
          'Configuration of the fixed-rate Wix service fee that is applied at checkout to each ticket sold.'
        )
        .optional(),
      salePeriod: z
        .object({
          startDate: z
            .date()
            .describe('Ticket sale start timestamp.')
            .optional()
            .nullable(),
          endDate: z
            .date()
            .describe('Ticket sale end timestamp.')
            .optional()
            .nullable(),
          hideNotOnSale: z
            .boolean()
            .describe("Whether to hide this ticket if it isn't on sale.")
            .optional(),
        })
        .describe('Ticket sale period.')
        .optional(),
      saleStatus: z
        .enum(['SALE_SCHEDULED', 'SALE_STARTED', 'SALE_ENDED'])
        .describe('Ticket sale status.')
        .optional(),
      state: z.array(z.enum(['INCLUDE_HIDDEN_NOT_ON_SALE'])).optional(),
      pricing: z
        .intersection(
          z.object({
            pricingType: z
              .enum(['STANDARD', 'DONATION'])
              .describe('Ticket pricing type.')
              .optional(),
          }),
          z.xor([
            z.object({
              fixedPrice: z.never().optional(),
              minPrice: z.never().optional(),
              pricingOptions: z.never().optional(),
            }),
            z.object({
              minPrice: z.never().optional(),
              pricingOptions: z.never().optional(),
              fixedPrice: z
                .object({
                  amount: z
                    .string()
                    .describe('*Deprecated:** Use `value` instead.')
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
                    )
                    .optional(),
                  value: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe('Ticket price which is read only.'),
            }),
            z.object({
              fixedPrice: z.never().optional(),
              pricingOptions: z.never().optional(),
              minPrice: z
                .object({
                  amount: z
                    .string()
                    .describe('*Deprecated:** Use `value` instead.')
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
                    )
                    .optional(),
                  value: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe('Min price per ticket, customizable.'),
            }),
            z.object({
              fixedPrice: z.never().optional(),
              minPrice: z.never().optional(),
              pricingOptions: z
                .object({
                  options: z
                    .array(
                      z.object({
                        _id: z
                          .string()
                          .describe('Ticket pricing option ID.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                        name: z
                          .string()
                          .describe('Ticket pricing option name.')
                          .min(1)
                          .max(200)
                          .optional()
                          .nullable(),
                        price: z
                          .object({
                            amount: z
                              .string()
                              .describe('*Deprecated:** Use `value` instead.')
                              .optional(),
                            currency: z
                              .string()
                              .describe(
                                '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
                              )
                              .optional(),
                            value: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
                              )
                              .optional()
                              .nullable(),
                          })
                          .describe('Ticket pricing option price.')
                          .optional(),
                      })
                    )
                    .optional(),
                })
                .describe('Ticket pricing options.'),
            }),
          ])
        )
        .describe('Ticket pricing.')
        .optional(),
    })
    .describe('Created ticket definition.')
    .optional(),
});
export const UpdateTicketDefinitionRequest = z.object({
  definitionId: z
    .string()
    .describe('Ticket definition ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  eventId: z
    .string()
    .describe('Event ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      definition: z
        .object({
          name: z
            .string()
            .describe('Ticket name.')
            .max(30)
            .optional()
            .nullable(),
          price: z
            .object({
              amount: z
                .string()
                .describe('*Deprecated:** Use `value` instead.')
                .optional(),
              currency: z
                .string()
                .describe(
                  '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
                )
                .optional(),
              value: z
                .string()
                .describe(
                  'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
                )
                .optional()
                .nullable(),
            })
            .describe('Ticket price.')
            .optional(),
          description: z
            .string()
            .describe('Ticket description.')
            .max(500)
            .optional()
            .nullable(),
          limited: z
            .boolean()
            .describe('Whether this ticket type is limited in quantity.')
            .optional(),
          quantity: z
            .number()
            .int()
            .describe('Limit for this ticket type.\n\n`NULL` for unlimited.')
            .optional()
            .nullable(),
          orderIndex: z
            .number()
            .int()
            .describe(
              'Custom sort index for manual tickets ordering implementation.'
            )
            .optional(),
          policy: z
            .string()
            .describe(
              'Policy information in plain text (as listed on the ticket).'
            )
            .max(1000)
            .optional()
            .nullable(),
          hidden: z
            .boolean()
            .describe(
              'Whether this ticket type is hidden to customers and cannot be purchased.'
            )
            .optional(),
          wixFeeConfig: z
            .object({
              type: z
                .enum(['FEE_ADDED', 'FEE_INCLUDED', 'FEE_ADDED_AT_CHECKOUT'])
                .optional(),
            })
            .describe(
              'Configuration of the fixed-rate Wix service fee that is applied to each ticket sold.\n\nDefault: `FEE_ADDED_AT_CHECKOUT`.'
            )
            .optional(),
          salePeriod: z
            .object({
              startDate: z
                .date()
                .describe('Ticket sale start timestamp.')
                .optional()
                .nullable(),
              endDate: z
                .date()
                .describe('Ticket sale end timestamp.')
                .optional()
                .nullable(),
              hideNotOnSale: z
                .boolean()
                .describe("Whether to hide this ticket if it isn't on sale.")
                .optional(),
            })
            .describe('Ticket sale period.')
            .optional(),
          pricing: z
            .intersection(
              z.object({
                pricingType: z.enum(['STANDARD', 'DONATION']).optional(),
              }),
              z.xor([
                z.object({
                  minPrice: z.never().optional(),
                  pricingOptions: z.never().optional(),
                  fixedPrice: z
                    .object({
                      amount: z
                        .string()
                        .describe('*Deprecated:** Use `value` instead.')
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
                        )
                        .optional(),
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe('Ticket price which is read only.'),
                }),
                z.object({
                  fixedPrice: z.never().optional(),
                  pricingOptions: z.never().optional(),
                  minPrice: z
                    .object({
                      amount: z
                        .string()
                        .describe('*Deprecated:** Use `value` instead.')
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
                        )
                        .optional(),
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe('Min price per ticket, customizable.'),
                }),
                z.object({
                  fixedPrice: z.never().optional(),
                  minPrice: z.never().optional(),
                  pricingOptions: z
                    .object({
                      options: z
                        .array(
                          z.object({
                            _id: z
                              .string()
                              .describe('Ticket pricing option ID.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional()
                              .nullable(),
                            name: z
                              .string()
                              .describe('Ticket pricing option name.')
                              .min(1)
                              .max(200)
                              .optional()
                              .nullable(),
                            price: z
                              .object({
                                amount: z
                                  .string()
                                  .describe(
                                    '*Deprecated:** Use `value` instead.'
                                  )
                                  .optional(),
                                currency: z
                                  .string()
                                  .describe(
                                    '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
                                  )
                                  .optional(),
                                value: z
                                  .string()
                                  .describe(
                                    'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
                                  )
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Ticket pricing option price.')
                              .optional(),
                          })
                        )
                        .optional(),
                    })
                    .describe('Ticket pricing options.'),
                }),
              ])
            )
            .describe('Ticket pricing.')
            .optional(),
        })
        .describe('Ticket definition data.')
        .optional(),
      fields: z
        .object({ paths: z.array(z.string()) })
        .describe(
          'Set of field paths, specifying which parts of ticket definition to update.'
        )
        .optional()
        .nullable(),
    })
    .describe('Ticket definition details to update.')
    .optional(),
});
export const UpdateTicketDefinitionResponse = z.object({
  definition: z
    .object({
      _id: z
        .string()
        .describe('Ticket definition ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      price: z
        .object({
          amount: z
            .string()
            .describe('*Deprecated:** Use `value` instead.')
            .optional(),
          currency: z
            .string()
            .describe(
              '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
            )
            .optional(),
          value: z
            .string()
            .describe(
              'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
            )
            .optional()
            .nullable(),
        })
        .describe('Ticket price.')
        .optional(),
      free: z
        .boolean()
        .describe('Whether the ticket is free (read only).')
        .optional(),
      name: z.string().describe('Ticket name.').optional(),
      description: z.string().describe('Ticket description.').optional(),
      limitPerCheckout: z
        .number()
        .int()
        .describe(
          'Limit of tickets that can be purchased per checkout.\nSet to 20 for unlimited ticket definition.'
        )
        .min(0)
        .max(20)
        .optional(),
      orderIndex: z.number().int().describe('Custom sort index.').optional(),
      policy: z
        .string()
        .describe(
          'Policy information plain text block, as printed on the ticket.'
        )
        .optional(),
      dashboard: z
        .object({
          hidden: z
            .boolean()
            .describe('Whether ticket is hidden and cannot be sold.')
            .optional(),
          sold: z
            .number()
            .int()
            .describe('Number of tickets sold and reserved.')
            .optional(),
          limited: z
            .boolean()
            .describe('Whether the ticket has limited quantity.')
            .optional(),
          quantity: z
            .number()
            .int()
            .describe('Ticket limit. `NULL` for unlimited ticket definitions.')
            .optional()
            .nullable(),
          unsold: z
            .number()
            .int()
            .describe(
              'Number of unsold tickets. `NULL` for unlimited ticket definitions.'
            )
            .optional()
            .nullable(),
          ticketsSold: z
            .number()
            .int()
            .describe('Number of tickets sold.')
            .optional(),
          ticketsReserved: z
            .number()
            .int()
            .describe('Number of tickets reserved.')
            .optional(),
        })
        .describe('Sensitive dashboard data.')
        .optional(),
      eventId: z
        .string()
        .describe('Event ID associated with the ticket.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      wixFeeConfig: z
        .object({
          type: z
            .enum(['FEE_ADDED', 'FEE_INCLUDED', 'FEE_ADDED_AT_CHECKOUT'])
            .describe('Fee calculation method.')
            .optional(),
        })
        .describe(
          'Configuration of the fixed-rate Wix service fee that is applied at checkout to each ticket sold.'
        )
        .optional(),
      salePeriod: z
        .object({
          startDate: z
            .date()
            .describe('Ticket sale start timestamp.')
            .optional()
            .nullable(),
          endDate: z
            .date()
            .describe('Ticket sale end timestamp.')
            .optional()
            .nullable(),
          hideNotOnSale: z
            .boolean()
            .describe("Whether to hide this ticket if it isn't on sale.")
            .optional(),
        })
        .describe('Ticket sale period.')
        .optional(),
      saleStatus: z
        .enum(['SALE_SCHEDULED', 'SALE_STARTED', 'SALE_ENDED'])
        .describe('Ticket sale status.')
        .optional(),
      state: z.array(z.enum(['INCLUDE_HIDDEN_NOT_ON_SALE'])).optional(),
      pricing: z
        .intersection(
          z.object({
            pricingType: z
              .enum(['STANDARD', 'DONATION'])
              .describe('Ticket pricing type.')
              .optional(),
          }),
          z.xor([
            z.object({
              fixedPrice: z.never().optional(),
              minPrice: z.never().optional(),
              pricingOptions: z.never().optional(),
            }),
            z.object({
              minPrice: z.never().optional(),
              pricingOptions: z.never().optional(),
              fixedPrice: z
                .object({
                  amount: z
                    .string()
                    .describe('*Deprecated:** Use `value` instead.')
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
                    )
                    .optional(),
                  value: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe('Ticket price which is read only.'),
            }),
            z.object({
              fixedPrice: z.never().optional(),
              pricingOptions: z.never().optional(),
              minPrice: z
                .object({
                  amount: z
                    .string()
                    .describe('*Deprecated:** Use `value` instead.')
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
                    )
                    .optional(),
                  value: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe('Min price per ticket, customizable.'),
            }),
            z.object({
              fixedPrice: z.never().optional(),
              minPrice: z.never().optional(),
              pricingOptions: z
                .object({
                  options: z
                    .array(
                      z.object({
                        _id: z
                          .string()
                          .describe('Ticket pricing option ID.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                        name: z
                          .string()
                          .describe('Ticket pricing option name.')
                          .min(1)
                          .max(200)
                          .optional()
                          .nullable(),
                        price: z
                          .object({
                            amount: z
                              .string()
                              .describe('*Deprecated:** Use `value` instead.')
                              .optional(),
                            currency: z
                              .string()
                              .describe(
                                '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
                              )
                              .optional(),
                            value: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
                              )
                              .optional()
                              .nullable(),
                          })
                          .describe('Ticket pricing option price.')
                          .optional(),
                      })
                    )
                    .optional(),
                })
                .describe('Ticket pricing options.'),
            }),
          ])
        )
        .describe('Ticket pricing.')
        .optional(),
    })
    .describe('Updated ticket definition.')
    .optional(),
});
export const DeleteTicketDefinitionRequest = z.object({
  eventId: z
    .string()
    .describe('Event ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .intersection(
      z.object({}),
      z.xor([
        z.object({ byId: z.never().optional(), all: z.never().optional() }),
        z.object({
          all: z.never().optional(),
          byId: z
            .object({
              definitionId: z.array(z.string()).min(1).max(100).optional(),
            })
            .describe('Ticket definitions to delete.'),
        }),
        z.object({
          byId: z.never().optional(),
          all: z.boolean().describe('Whether to delete all event tickets.'),
        }),
      ])
    )
    .describe('Details of tickets to delete.')
    .optional(),
});
export const DeleteTicketDefinitionResponse = z.object({});
export const ChangeCurrencyRequest = z.object({
  options: z.object({
    eventId: z
      .string()
      .describe('Event ID.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional(),
    currency: z
      .string()
      .describe(
        'Event currency, in 3-letter [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
      )
      .min(3)
      .max(3),
  }),
});
export const ChangeCurrencyResponse = z.object({});
